import moment from 'moment';

export const PERCENTAGE_INTERVALS = [0, 25, 50, 75];
export const SMALL_PERCENTAGE_TICKS = [50, 55, 60, 65, 70, 75, 80, 85, 90, 95];
export const LARGE_PERCENTAGE_TICKS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90];

export const calculateMovingAverage = (
  data: {
    date: string;
    value: number;
  }[],
  windowSize: number,
) =>
  data.map((d, index) => {
    const start = Math.max(0, index - windowSize + 1);
    const windowData = data.slice(start, index + 1);
    const average = windowData.reduce((sum, item) => sum + item.value, 0) / windowData.length;

    return {
      ...d,
      movingAverage: average,
    };
  });

/**
 * Returns a domain [min, max] where min adjusts in 25% steps
 * based on the minimum value in the data.
 */
export const getQuarterDomain = (data: number[]): [number, number] => {
  if (data.length === 0) {
    return [0, 1]; // fallback domain
  }
  const maxValue = Math.max(...data);

  const lowerBound =
    [...PERCENTAGE_INTERVALS].reverse().find((threshold) => data.every((value) => value >= threshold)) ?? 0;

  const upperBound = PERCENTAGE_INTERVALS.find((threshold) => maxValue <= threshold) ?? 100;

  return [lowerBound, upperBound];
};

export const calculatePercentageBuckets = (
  data: { date: string; value: number }[],
  fromDate: string = moment().format('YYYY-MM-DD'),
) => {
  const buckets = [
    { name: '0', floor: 0, ceiling: 30 },
    { name: '1', floor: 31, ceiling: 60 },
    { name: '2', floor: 61, ceiling: 90 },
    { name: '3', floor: 91, ceiling: 120 },
    { name: '4', floor: 121, ceiling: 150 },
    { name: '5', floor: 151, ceiling: 180 },
    { name: '6-8', floor: 181, ceiling: 270 },
    { name: '9-11', floor: 271, ceiling: 365 },
    { name: '12+', floor: 366, ceiling: Infinity },
  ];
  const totalValue = data.reduce((sum, item) => sum + item.value, 0);
  const bucketValues = buckets.map((bucket) => {
    const bucketValue = data
      .filter((item) => {
        const daysInPast = moment(fromDate).diff(moment(item.date), 'days');
        const lowerThanCeiling = daysInPast <= bucket.ceiling;
        const higherThanFloor = daysInPast >= bucket.floor;
        return lowerThanCeiling && higherThanFloor;
      })
      .reduce((sum, item) => sum + item.value, 0);
    return { ...bucket, value: bucketValue, percentage: (bucketValue / totalValue) * 100 };
  });

  return bucketValues;
};
